import testService from "@/services/testService";
import {
  getFlowType,
  TEST_FLOW,
  IDTEL_STEP_COUNT,
} from "@/components/corregir-test/constants/testCatalog";
import { extractTestRecordId } from "@/components/corregir-test/utils/normalizeTestIds";

/** IDTEL persists its row only on the final step; earlier steps are intermediate. */
const IDTEL_LAST_STEP = IDTEL_STEP_COUNT - 1;

// Owned exclusively by whichever CorregirTestWorkspace is mounted (see its
// context.studentGradeId / context.semester watchers) — the Utopie module
// sets these, the free/anonymous Shell's Workspace asserts them back to
// null on mount, closing any stale-leak window between the two.
let correctionStudentId = null;
let correctionSemester = null;

export function setCorrectionStudent(studentGradeId) {
  correctionStudentId = studentGradeId == null ? null : studentGradeId;
}

// null = no manual override; submitCorrection falls back to defaultSemester().
// A manual override exists because the legacy DocumentTest.vue interface
// lets professionals pick the semester explicitly (e.g. to log a test
// against last semester after it's over) — CorrectTestsModule mirrors that
// with its own <select>, defaulted via defaultSemester().
export function setCorrectionSemester(semester) {
  correctionSemester = semester == null ? null : semester;
}

export function defaultSemester() {
  // Mirrors DocumentTest.vue's legacy default (getMonth() is 0-indexed, so
  // >= 6 means July onward) — must agree with StudentGradeDocumentType.
  // get_required_student_tests()'s server-side `month > 6` check, or a
  // linked test silently won't be picked up by the informe.
  return new Date().getMonth() >= 6 ? 2 : 1;
}

function isFailedCorrection(result) {
  return (
    !result ||
    result.tests === "not 200" ||
    result.error ||
    (typeof result.tests === "string" && result.tests.endsWith("_paused"))
  );
}

function isIntermediateCorrectionStep(payload) {
  if (!payload || payload.step == null) return false;
  const testValue = parseInt(payload.test, 10);
  const step = parseInt(payload.step, 10);
  return getFlowType(testValue) === TEST_FLOW.IDTEL && step < IDTEL_LAST_STEP;
}

function buildError(result, fallbackMessage) {
  let message = fallbackMessage;
  if (result && result.message) {
    message = result.message;
  }
  const error = new Error(message);
  error.responsePayload = result;
  return error;
}

const MAX_VALIDATION_ERRORS = 3;

// Mensaje de validación que RedPsi devuelve en `redpsi_body` para los tests
// que validan antes de guardar (18-24): `{"message": "campo: texto; ..."}`.
// Solo se toman los de esa forma; cualquier otra cosa (una excepción, HTML)
// sigue siendo texto crudo que no es para el usuario.
function redpsiValidationMessage(body) {
  let parsed = null;
  try {
    parsed = JSON.parse(body);
  } catch (e) {
    return "";
  }
  const message = parsed && typeof parsed.message === "string" ? parsed.message : "";
  if (!/^\w+: /.test(message)) return "";
  // Un error por campo, separados por "; ". Si rechaza muchos a la vez (un
  // formulario vacío) basta con los primeros: la lista entera no se lee.
  const errores = message.split(/; (?=\w+: )/);
  const visibles = errores.slice(0, MAX_VALIDATION_ERRORS).join("; ");
  const resto = errores.length - MAX_VALIDATION_ERRORS;
  // "__all__" es el error del formulario entero, sin un campo.
  return visibles.replace(/(^|; )__all__: /g, "$1") + (resto > 0 ? ` (y ${resto} más)` : "");
}

// Surfaces whatever the backend actually explains (auth/forbidden/paused
// responses already carry a friendly `message`) instead of axios's generic
// "Request failed with status code NNN". A RedPsi rejection (`tests: "not
// 200"`) only carries `redpsi_body`, RedPsi's raw response text — not meant
// for end users, so that case gets a generic-but-honest fallback instead of
// being passed through verbatim, unless it is a validation message.
function buildRequestError(err) {
  const data = err && err.response && err.response.data;
  let message = "No se pudo corregir el test. Intenta nuevamente.";
  if (data && typeof data.message === "string" && data.message) {
    message = data.message;
  } else if (data && data.tests === "not 200") {
    const validation = redpsiValidationMessage(data.redpsi_body);
    message = validation
      ? `El servidor de corrección rechazó los datos: ${validation}`
      : "El servidor de corrección rechazó los datos ingresados. Revisa que todos los puntajes estén completos y dentro de rango.";
  }
  const error = new Error(message);
  error.responsePayload = data;
  error.cause = err;
  if (data && data.tests === "no_uses") {
    error.outOfUses = true;
    notifyOutOfUses();
  }
  return error;
}

// Quedarse sin usos no es un error de datos: no se arregla reintentando, sino
// comprando usos o ganándolos con una misión. Cada formulario atrapa su propio
// error y lo muestra como toast, así que en vez de tocarlos todos el aviso se
// resuelve acá y el workspace se suscribe para abrir el camino de salida.
// Sin suscriptor no pasa nada: el toast del formulario sigue siendo el
// comportamiento de siempre.
let outOfUsesHandler = null;

export function setOutOfUsesHandler(handler) {
  outOfUsesHandler = typeof handler === "function" ? handler : null;
}

function notifyOutOfUses() {
  if (!outOfUsesHandler) return;
  try {
    outOfUsesHandler();
  } catch (e) {
    // El aviso jamás debe tapar el error original que se está construyendo.
  }
}

/**
 * Builds the POST body expected by correct_test_no_student/.
 * Forms in later phases supply `answers`; the adapter keeps step/redpsi_id wiring.
 */
export function createCorrectionPayload({
  testValue,
  answers = {},
  step,
  redpsi_id,
  semester = 1,
  previous_answer,
}) {
  const payload = {
    test: testValue,
    answers,
    semester,
  };
  if (step != null) payload.step = step;
  if (redpsi_id != null) payload.redpsi_id = redpsi_id;
  // Acumulador de respuestas de los pasos anteriores: el backend guarda
  // `previous_answer` + el paso actual bajo 'step-N', así que sin esto el Test
  // solo conserva el último paso (ver runIdtelCorrection).
  if (previous_answer != null) payload.previous_answer = previous_answer;
  return payload;
}

/**
 * Adds the correction context (student + semester) to a payload. Exported for
 * the one caller that can't go through submitCorrection: the patch
 * DynamicTestForm applies to testService.send_test so the old form-test/
 * components can be reused. Those call the service directly and expect a
 * `.then(data => …)` with the raw payload, while submitCorrection throws on
 * failure — so they inject the context and post on their own.
 *
 * Without a context (the free/anonymous product) the payload is returned
 * untouched: the Test is then created with no student_grade, which is exactly
 * right there.
 */
export function withCorrectionContext(payload) {
  if (correctionStudentId == null) return payload;
  return {
    ...payload,
    student_id: correctionStudentId,
    semester: correctionSemester || defaultSemester(),
  };
}

export async function submitCorrection(payload) {
  const body = withCorrectionContext(payload);
  let result;
  try {
    result = await testService.correctTestNoStudent(body);
  } catch (err) {
    throw buildRequestError(err);
  }
  if (isFailedCorrection(result)) {
    throw buildError(result, "No se pudo corregir el test. Revisa los datos.");
  }
  if (isIntermediateCorrectionStep(payload)) {
    return result;
  }
  const id = extractTestRecordId(result);
  if (!id) {
    throw buildError(
      result,
      "El test se corrigió pero no se recibió su ID. Recarga e intenta de nuevo."
    );
  }
  if (result.id) return result;
  return Object.assign({}, result, { id: id });
}

/** TEPROSIF step 0 — RedPsi returns process selects for step 1. */
export async function submitTeprosifStep0({ testValue, answers, semester = 1 }) {
  return submitCorrection(
    createCorrectionPayload({ testValue, answers, step: 0, semester })
  );
}

/** TEPROSIF step 1 — persists the fully corrected test row. */
export async function submitTeprosifStep1({
  testValue,
  answers,
  redpsi_id,
  semester = 1,
}) {
  return submitCorrection(
    createCorrectionPayload({
      testValue,
      answers,
      step: 1,
      redpsi_id,
      semester,
    })
  );
}

/**
 * Runs the full IDTEL multi-step correction (steps 0 … IDTEL_LAST_STEP).
 * `stepsAnswers` is an array of IDTEL_STEP_COUNT answer objects, one per step.
 */
export async function runIdtelCorrection({
  testValue,
  stepsAnswers,
  semester = 1,
  onStepComplete,
}) {
  let redpsi_id;
  let lastResult = null;
  // El Test solo se persiste en el último paso, y el backend arma su `data`
  // desde el `previous_answer` que le mandemos más el paso actual. Si no lo
  // encadenamos (como sí hace el formulario antiguo Idtel.vue), el Test queda
  // guardado con un solo dominio de 15: el puntaje sale bien porque RedPsi
  // lleva el estado por redpsi_id, pero se pierde el registro de lo que
  // respondió el estudiante y el test deja de poder editarse.
  let previousAnswer;

  for (let step = 0; step <= IDTEL_LAST_STEP; step += 1) {
    const answers = stepsAnswers[step] || {};
    // eslint-disable-next-line no-await-in-loop
    const data = await submitCorrection(
      createCorrectionPayload({
        testValue,
        answers,
        step,
        redpsi_id,
        semester,
        previous_answer: previousAnswer,
      })
    );

    if (data && data.data) previousAnswer = data.data;

    if (step < IDTEL_LAST_STEP) {
      redpsi_id = data.redpsi_id;
      if (typeof onStepComplete === "function") {
        onStepComplete(step, data);
      }
    } else {
      lastResult = data;
    }
  }

  return lastResult;
}

/**
 * Dispatches to the right flow helper based on TestEnum value.
 * Single-step tests pass the full answers object once.
 */
export async function correctTestByFlow({
  testValue,
  answers,
  stepsAnswers,
  redpsi_id,
  step,
  semester = 1,
  onIdtelStepComplete,
}) {
  const flow = getFlowType(testValue);

  if (flow === TEST_FLOW.TEPROSIF) {
    if (step === 1) {
      return submitTeprosifStep1({
        testValue,
        answers,
        redpsi_id,
        semester,
      });
    }
    return submitTeprosifStep0({ testValue, answers, semester });
  }

  if (flow === TEST_FLOW.IDTEL) {
    if (!Array.isArray(stepsAnswers) || stepsAnswers.length !== IDTEL_STEP_COUNT) {
      throw new Error(`IDTEL requiere ${IDTEL_STEP_COUNT} pasos de respuestas.`);
    }
    return runIdtelCorrection({
      testValue,
      stepsAnswers,
      semester,
      onStepComplete: onIdtelStepComplete,
    });
  }

  return submitCorrection(
    createCorrectionPayload({ testValue, answers, semester })
  );
}

export function loadPreviousAnswers(testId) {
  return testService.getPreviousAnswers({ id: testId });
}

export function downloadCorrectedReport(testIds) {
  return testService.correctedTestsReport({ test_ids: testIds });
}
